import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Trash2, Minus, Plus, Loader2, ArrowRight, CreditCard, Smartphone, ArrowLeft, CheckCircle2, Copy, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const WHATSAPP_PHONE = "237699451241";
const WHATSAPP_DISPLAY = "+237 699 451 241";
const WHATSAPP_LINK = (msg) => `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(msg)}`;

export default function CartDrawer() {
  const { items, setQuantity, remove, count, subtotal, open, setOpen, clear } = useCart();
  const [step, setStep] = useState("cart");           // cart | pay | done
  const [tab, setTab] = useState("card");             // card | manual
  const [methods, setMethods] = useState(null);
  const [busy, setBusy] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", method: "mtn", reference: "", note: "" });

  useEffect(() => {
    if (open && !methods) {
      api.get("/payments/methods").then(({ data }) => setMethods(data)).catch(() => {});
    }
  }, [open, methods]);

  useEffect(() => {
    if (!open) {
      // reset when drawer fully closes
      const t = setTimeout(() => {
        setStep("cart");
        setTab("card");
        setWhatsappUrl("");
        setForm({ name: "", email: "", phone: "", method: "mtn", reference: "", note: "" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [open]);

  const checkoutCard = async () => {
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/store/checkout", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/store/manual-order", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        name: form.name, email: form.email, phone: form.phone,
        method: form.method, reference: form.reference, note: form.note,
      });
      // Build a WhatsApp confirmation URL — user will tap the button on the
      // success screen to open it (avoids popup-blocker issues after await).
      const cart = items.map((i) => `${i.quantity}× ${i.title}`).join(", ");
      const msg = `Hello Apostle Blaise Judah 🙏\n\nI just placed order #${data.order_id} on the website.\nItems: ${cart}\nTotal: $${data.total.toFixed(2)}\nPayment method: ${labelForMethod(form.method)}\nRef: ${form.reference}\n\nI'm attaching my payment screenshot for confirmation.`;
      setWhatsappUrl(WHATSAPP_LINK(msg));
      clear();
      setStep("done");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const labelForMethod = (code) => ({
    mtn: "MTN Mobile Money",
    orange: "Orange Money",
    bank: "Bank Transfer",
    crypto: "Crypto (USDT · BTC)",
  }[code] || code);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-ink/80 backdrop-blur-sm z-[70]"
            data-testid="cart-backdrop"
          />
          {/* Drawer */}
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 h-full w-full sm:w-[460px] bg-ink border-l border-gold-500/30 z-[80] flex flex-col"
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gold-500/20">
              <div className="flex items-center gap-3">
                {step === "pay" && (
                  <button
                    onClick={() => setStep("cart")}
                    data-testid="cart-back"
                    className="text-white/60 hover:text-gold-300"
                    aria-label="Back"
                  >
                    <ArrowLeft size={18} />
                  </button>
                )}
                <ShoppingBag size={20} className="text-gold-500" />
                <div>
                  <p className="font-serif text-lg text-white">
                    {step === "cart" ? "Your Cart" : step === "pay" ? "Checkout" : "Thank you"}
                  </p>
                  <p className="text-xs text-white/50">
                    {step === "cart"
                      ? (count === 0 ? "Empty" : `${count} item${count > 1 ? "s" : ""}`)
                      : step === "pay"
                        ? `Total · $${subtotal.toFixed(2)}`
                        : "Order received"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-close"
                className="text-white/60 hover:text-gold-300 transition-colors"
                aria-label="Close cart"
              >
                <X size={22} />
              </button>
            </div>

            {/* Body */}
            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="cart-items">
                  {items.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center px-4" data-testid="cart-empty">
                      <ShoppingBag size={44} className="text-gold-500/40 mb-4" strokeWidth={1.2} />
                      <p className="font-serif text-xl text-white">Your cart is empty</p>
                      <p className="mt-2 text-sm text-white/50">Add books, music, or resources from the store.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gold-500/10">
                      {items.map((it) => (
                        <li key={it.slug} className="py-4 flex gap-4" data-testid={`cart-line-${it.slug}`}>
                          <div className="w-16 h-16 shrink-0 overflow-hidden border border-gold-500/20 bg-ink-paper">
                            {it.image ? (
                              <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
                            ) : null}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-serif text-white leading-snug line-clamp-2">{it.title}</p>
                            <p className="text-[11px] uppercase tracking-[0.15em] text-gold-400 mt-1">{it.type}</p>
                            <div className="mt-2 flex items-center justify-between gap-2">
                              <div className="flex items-center border border-gold-500/25">
                                <button
                                  onClick={() => setQuantity(it.slug, it.quantity - 1)}
                                  data-testid={`qty-dec-${it.slug}`}
                                  className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-gold-300"
                                  aria-label="Decrease"
                                >
                                  <Minus size={12} />
                                </button>
                                <span className="w-8 text-center text-sm text-white" data-testid={`qty-${it.slug}`}>{it.quantity}</span>
                                <button
                                  onClick={() => setQuantity(it.slug, it.quantity + 1)}
                                  data-testid={`qty-inc-${it.slug}`}
                                  className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-gold-300"
                                  aria-label="Increase"
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                              <p className="text-sm font-semibold text-gold-300">${(it.price * it.quantity).toFixed(2)}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => remove(it.slug)}
                            data-testid={`remove-${it.slug}`}
                            className="text-white/40 hover:text-red-300 self-start"
                            aria-label="Remove"
                          >
                            <Trash2 size={15} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {items.length > 0 && (
                  <div className="border-t border-gold-500/20 px-6 py-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-[0.2em] text-white/50">Subtotal</span>
                      <span className="font-serif text-2xl text-white" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                    </div>
                    <p className="text-[11px] text-white/45">Choose card, Mobile Money, Bank or Crypto on the next step.</p>
                    <button
                      onClick={() => setStep("pay")}
                      data-testid="checkout-btn"
                      className="w-full inline-flex items-center justify-center gap-2 bg-gold-500 text-ink hover:bg-gold-400 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] transition-colors"
                    >
                      Continue to Payment <ArrowRight size={15} />
                    </button>
                    <button
                      onClick={clear}
                      data-testid="cart-clear"
                      className="w-full text-[11px] uppercase tracking-[0.2em] text-white/40 hover:text-gold-300 transition-colors"
                    >
                      Clear cart
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Payment step */}
            {step === "pay" && (
              <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="cart-pay">
                {/* Tabs */}
                <div className="grid grid-cols-2 border border-gold-500/25 mb-5" data-testid="pay-tabs">
                  <button
                    onClick={() => setTab("card")}
                    data-testid="pay-tab-card"
                    className={`flex items-center justify-center gap-2 py-2.5 text-[11px] uppercase tracking-[0.15em] transition-colors ${
                      tab === "card" ? "bg-gold-500 text-ink font-semibold" : "text-white/70 hover:text-gold-300"
                    }`}
                  >
                    <CreditCard size={14} /> Card
                  </button>
                  <button
                    onClick={() => setTab("manual")}
                    data-testid="pay-tab-manual"
                    className={`flex items-center justify-center gap-2 py-2.5 text-[11px] uppercase tracking-[0.15em] transition-colors ${
                      tab === "manual" ? "bg-gold-500 text-ink font-semibold" : "text-white/70 hover:text-gold-300"
                    }`}
                  >
                    <Smartphone size={14} /> Mobile · Bank · Crypto
                  </button>
                </div>

                {tab === "card" && (
                  <div className="space-y-4" data-testid="pay-panel-card">
                    <div className="border border-gold-500/20 bg-ink-paper/40 p-4">
                      <p className="text-sm text-white/70 leading-relaxed">
                        Pay by international card via Stripe. You'll be redirected to a secure hosted checkout.
                      </p>
                      <div className="mt-4 flex items-center justify-between border-t border-gold-500/15 pt-4">
                        <span className="text-[11px] uppercase tracking-[0.2em] text-white/50">Total</span>
                        <span className="font-serif text-2xl text-gold-300">${subtotal.toFixed(2)}</span>
                      </div>
                    </div>
                    <button
                      onClick={checkoutCard}
                      disabled={busy}
                      data-testid="pay-card-submit"
                      className="w-full inline-flex items-center justify-center gap-2 bg-gold-500 text-ink hover:bg-gold-400 disabled:opacity-60 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] transition-colors"
                    >
                      {busy ? (<><Loader2 size={15} className="animate-spin" /> Redirecting to Stripe…</>) : (<>Pay with Card <ArrowRight size={15} /></>)}
                    </button>
                    <p className="text-center text-[11px] text-white/40">Test card 4242 4242 4242 4242 · any future date · any CVC</p>
                  </div>
                )}

                {tab === "manual" && (
                  <div className="space-y-5" data-testid="pay-panel-manual">
                    {/* Payment channels */}
                    <div className="space-y-2">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gold-500">Pay to · {methods?.account_name || "House of Blessing Ministries"}</p>
                      {(methods?.methods || []).map((m) => (
                        <div key={m.code} className="flex items-center justify-between border border-gold-500/20 bg-ink-paper/40 px-4 py-2.5">
                          <div className="min-w-0 pr-3">
                            <p className="text-sm text-white">{m.label}</p>
                            <p className="text-xs text-gold-300 truncate">{m.detail}</p>
                          </div>
                          {m.code !== "bank" && m.code !== "crypto" && (
                            <button
                              onClick={() => copy(m.detail)}
                              className="text-white/50 hover:text-gold-400 shrink-0"
                              data-testid={`copy-${m.code}`}
                              aria-label={`Copy ${m.label}`}
                            >
                              <Copy size={15} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* WhatsApp screenshot instruction */}
                    <div className="border border-[#0b8a4f]/60 bg-[#0b8a4f]/10 p-4 space-y-2">
                      <div className="flex items-center gap-2 text-[#3ecf7e]">
                        <MessageCircle size={16} />
                        <p className="text-[11px] uppercase tracking-[0.2em] font-semibold">Confirm via WhatsApp</p>
                      </div>
                      <p className="text-xs text-white/75 leading-relaxed">
                        Send your payment, then <span className="text-white font-semibold">take a screenshot and share it via WhatsApp to {WHATSAPP_DISPLAY}</span>. Submit the form below and we'll open WhatsApp for you.
                      </p>
                    </div>

                    <form onSubmit={submitManual} className="space-y-3" data-testid="pay-manual-form">
                      <div className="grid grid-cols-2 gap-3">
                        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pay-name" className="bg-ink border border-gold-500/25 text-white placeholder:text-white/40 px-3 py-2 text-sm outline-none focus:border-gold-400" />
                        <input required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="pay-phone" className="bg-ink border border-gold-500/25 text-white placeholder:text-white/40 px-3 py-2 text-sm outline-none focus:border-gold-400" />
                      </div>
                      <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pay-email" className="w-full bg-ink border border-gold-500/25 text-white placeholder:text-white/40 px-3 py-2 text-sm outline-none focus:border-gold-400" />
                      <div className="grid grid-cols-2 gap-3">
                        <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="pay-method" className="bg-ink border border-gold-500/25 text-white/90 px-3 py-2 text-sm outline-none focus:border-gold-400">
                          <option value="mtn">MTN MoMo</option>
                          <option value="orange">Orange Money</option>
                          <option value="bank">Bank Transfer</option>
                          <option value="crypto">Crypto (USDT · BTC)</option>
                        </select>
                        <input required placeholder="Transaction ref / tx hash" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="pay-reference" className="bg-ink border border-gold-500/25 text-white placeholder:text-white/40 px-3 py-2 text-sm outline-none focus:border-gold-400" />
                      </div>
                      <textarea placeholder="Note (optional)" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pay-note" className="w-full bg-ink border border-gold-500/25 text-white placeholder:text-white/40 px-3 py-2 text-sm outline-none focus:border-gold-400 resize-none" />
                      <button type="submit" disabled={busy} data-testid="pay-manual-submit" className="w-full inline-flex items-center justify-center gap-2 bg-gold-500 text-ink hover:bg-gold-400 disabled:opacity-60 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] transition-colors">
                        {busy ? (<><Loader2 size={15} className="animate-spin" /> Submitting…</>) : (<>Submit &amp; Open WhatsApp · ${subtotal.toFixed(2)}</>)}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Done */}
            {step === "done" && (
              <div className="flex-1 overflow-y-auto px-6 py-8 text-center" data-testid="cart-done">
                <CheckCircle2 size={52} className="text-gold-400 mx-auto" />
                <p className="mt-6 font-serif text-2xl text-white">Order received</p>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Thank you. To confirm your payment, tap the button below to open WhatsApp and <span className="text-white font-semibold">send us a screenshot of your payment</span> to {WHATSAPP_DISPLAY}. We confirm within 24 hours and unlock your items or dispatch your order.
                </p>
                <a
                  href={whatsappUrl || WHATSAPP_LINK("Hello Apostle — I just placed a manual order on the website and I'm attaching my payment screenshot.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cart-done-whatsapp"
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-[#0b8a4f] hover:bg-[#0a7a45] text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] transition-colors"
                >
                  <MessageCircle size={15} /> Send Payment Screenshot on WhatsApp
                </a>
                <button
                  onClick={() => setOpen(false)}
                  className="mt-4 block mx-auto text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-gold-300"
                  data-testid="cart-done-close"
                >
                  Close
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
